// Global
import React, { useState, useRef, ReactNode, useEffect } from 'react';
import clsx from 'clsx';

// Local
import styles from './ContentTabs.module.scss';
import { Themes } from 'lib/themes';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Preamble, PreambleProps } from 'components/helpers/Molecules/Preamble/Preamble';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';

export interface TabItem {
  id: string;
  heading: React.ReactNode;
  content: ReactNode;
  image?: ReactNode;
}

export interface ContentTabsProps {
  className?: string;
  preamble?: PreambleProps;
  showPreamble?: boolean;
  tabs?: TabItem[];
  theme?: Themes;
}

export const ContentTabs = ({
  className,
  preamble,
  showPreamble,
  tabs,
  theme,
}: ContentTabsProps) => {
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const isEditing = useIsEditing();
  const [activeTab, setActiveTab] = useState<TabItem | null>(
    tabs && tabs.length > 0 ? tabs[0] : null
  );
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [jsEnabled, setJsEnabled] = useState(false);

  // Check scroll position to show/hide gradients
  const handleScroll = () => {
    const container = tabListRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      // No scrolling needed - hide both gradients
      setShowLeftGradient(false);
      setShowRightGradient(false);
      return;
    }

    // Scrolled from the start - show left gradient
    const isScrolledFromStart = scrollLeft > 5;

    // Has content to scroll right - show right gradient
    const hasMoreContentRight = scrollLeft < scrollWidth - clientWidth - 5;

    setShowLeftGradient(isScrolledFromStart);
    setShowRightGradient(hasMoreContentRight);
  };

  // Enable JS-based features after mount (progressive enhancement for SEO)
  useEffect(() => {
    setJsEnabled(true);
    document.documentElement.classList.add('js-enabled');
  }, []);

  // Set up scroll listener and initial check
  useEffect(() => {
    const container = tabListRef.current;
    if (!container) return;

    handleScroll(); // Initial check
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [tabs]);

  // Keyboard navigation handler
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    if (!tabs || tabs.length === 0) return;

    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const newTab = tabs[newIndex];
    if (newTab) {
      handleTabChange(newTab);
      // Focus the new tab
      setTimeout(() => {
        tabRefs.current[newTab.id]?.focus();
      }, 0);
    }
  };
  // Tab change handler
  const handleTabChange = (tab: TabItem) => {
    setActiveTab(tab);
    // Scroll tab into view
    const tabElement = tabRefs.current[tab.id];
    tabElement?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
  };

  return (
    <BrandAndThemeProvider theme={theme}>
      <div
        className={clsx(
          styles.contentTabs,
          {
            [styles.twoTabsOnly]: tabs?.length === 2,
            [styles.threeTabsOnly]: tabs?.length === 3,
          },
          className
        )}
        data-component="authorable/shared/content/content-tabs"
      >
        <div className={styles.contentContainer}>
          {/* Preamble Section */}
          {(showPreamble || isEditing) && (
            <div className={styles.preambleSection}>{preamble && <Preamble {...preamble} />}</div>
          )}
          <div className={styles.tabsContent}>
            <div
              className={styles.tabNavigation}
              role="tablist"
              aria-label="Content tabs"
              aria-orientation="horizontal"
            >
              {/* Left gradient */}
              {showLeftGradient && (
                <div className={clsx(styles.scrollGradient, styles.left)} aria-hidden="true" />
              )}

              {/* Right gradient */}
              {showRightGradient && (
                <div className={clsx(styles.scrollGradient, styles.right)} aria-hidden="true" />
              )}

              <div
                className={styles.tabList}
                ref={tabListRef}
                aria-live="polite"
                aria-atomic="false"
              >
                {tabs?.map((tab, index) => {
                  const isActive = tab.id === activeTab?.id;
                  return (
                    <button
                      key={tab.id}
                      ref={(el) => {
                        tabRefs.current[tab.id] = el;
                      }}
                      className={clsx(styles.tabButton, {
                        [styles.active]: isActive,
                      })}
                      id={`tab-${tab.id}`}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`tabpanel-${tab.id}`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => handleTabChange(tab)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      type="button"
                    >
                      <span className={styles.tabLabel}>{tab.heading}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className={styles.tabPanels}>
              {isEditing
                ? // In editing mode: render ALL panels (keep RTE chrome in DOM)
                  tabs?.map((tab) => {
                    const isActive = tab.id === activeTab?.id;
                    return (
                      <div
                        key={tab.id}
                        id={`tabpanel-${tab.id}`}
                        className={clsx(styles.tabPanel, {
                          [styles.active]: isActive,
                          [styles.hidden]: !isActive,
                        })}
                        role="tabpanel"
                        aria-labelledby={`tab-${tab.id}`}
                        aria-hidden={!isActive}
                        tabIndex={0}
                      >
                        {tab.image && <span className={styles.tabImage}>{tab.image}</span>}
                        {tab.content && <div className={styles.tabContent}>{tab.content}</div>}
                      </div>
                    );
                  })
                : // In normal mode: render ALL panels for SEO, but hide inactive ones only after JS loads
                  tabs?.map((tab) => {
                    const isActive = tab.id === activeTab?.id;
                    return (
                      <div
                        key={tab.id}
                        id={`tabpanel-${tab.id}`}
                        className={clsx(styles.tabPanel, {
                          [styles.active]: isActive,
                          [styles.hidden]: !isActive && jsEnabled,
                        })}
                        role="tabpanel"
                        aria-labelledby={`tab-${tab.id}`}
                        aria-hidden={!isActive}
                        tabIndex={0}
                      >
                        {tab.image && <span className={styles.tabImage}>{tab.image}</span>}
                        {tab.content && <div className={styles.tabContent}>{tab.content}</div>}
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      </div>
    </BrandAndThemeProvider>
  );
};
